/**
 * Zustand store for local SQLite database state
 *
 * Manages:
 * - Database connection state
 * - Cached classes and students
 * - Sheet number → name mapping for quick lookups
 * - Auto-save with debouncing
 */

import { create } from "zustand";
import { Database } from "sql.js";
import type {
  LocalClass,
  LocalStudent,
  DatabaseState,
  CreateClassInput,
  UpdateStudentInput,
} from "@/types/local-db.types";
import {
  createNewDatabase,
  getAllClasses,
  getStudentsByClass,
  createClass,
  updateClass,
  deleteClass,
  createStudent,
  updateStudent,
  deleteStudent,
  bulkCreateStudents,
} from "@/lib/local-db";
import {
  pickAndCheckDatabaseFile,
  openDatabaseWithPassword,
  saveDatabaseFile,
  createNewDatabaseFile,
  isFileSystemAccessSupported,
  type FileCheckResult,
  type FileSystemFileHandleExt,
} from "@/lib/file-system";

// ============================================================================
// Types
// ============================================================================

interface LocalDbState {
  // Database connection
  db: Database | null;
  fileHandle: FileSystemFileHandleExt | null;
  fileName: string | null;
  isConnected: boolean;
  isDirty: boolean;
  lastSaved: string | null;

  // Encryption state
  isEncrypted: boolean;
  password: string | null; // In memory only, never persisted

  // Pending file check result (for encryption flow)
  pendingFileCheck: FileCheckResult | null;

  // Failed password attempts (for rate limiting)
  failedPasswordAttempts: number;

  // Cached data
  classes: LocalClass[];
  students: LocalStudent[];
  selectedClassId: string | null;

  // Sheet number mapping for quick lookups
  sheetNumberMap: Map<string, { name: string; id: string }>;

  // Loading/error states
  loading: boolean;
  error: string | null;
}

interface LocalDbActions {
  // Database operations
  openDatabase: () => Promise<void>;
  createDatabase: () => Promise<void>;
  saveDatabase: () => Promise<void>;
  closeDatabase: () => void;

  // Encryption operations
  checkDatabaseFile: () => Promise<FileCheckResult | null>;
  openEncryptedDatabase: (password: string) => Promise<void>;
  createEncryptedDatabase: (password: string) => Promise<void>;
  enableEncryption: (password: string) => Promise<void>;
  lockDatabase: () => void;
  clearPendingFileCheck: () => void;
  clearPassword: () => void;

  // Class operations
  selectClass: (classId: string | null) => void;
  addClass: (input: CreateClassInput) => LocalClass;
  editClass: (id: string, input: Partial<CreateClassInput>) => void;
  removeClass: (id: string) => void;

  // Student operations
  addStudent: (classId: string, name: string, sheetNumber?: string) => LocalStudent;
  editStudent: (id: string, input: UpdateStudentInput) => void;
  removeStudent: (id: string) => void;
  importStudents: (classId: string, students: Array<{ name: string; sheetNumber?: string }>) => LocalStudent[];

  // Lookup helpers
  getStudentBySheetNumber: (sheetNumber: string) => { name: string; id: string } | null;
  refreshSheetNumberMap: () => void;

  // State management
  markDirty: () => void;
  clearError: () => void;
}

type LocalDbStore = LocalDbState & LocalDbActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: LocalDbState = {
  db: null,
  fileHandle: null,
  fileName: null,
  isConnected: false,
  isDirty: false,
  lastSaved: null,
  isEncrypted: false,
  password: null,
  pendingFileCheck: null,
  failedPasswordAttempts: 0,
  classes: [],
  students: [],
  selectedClassId: null,
  sheetNumberMap: new Map(),
  loading: false,
  error: null,
};

// ============================================================================
// Auto-save debounce
// ============================================================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let isSaving = false;
const AUTO_SAVE_DELAY = 2000; // 2 seconds

function scheduleSave(getStore: () => LocalDbStore) {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(async () => {
    // Prevent concurrent save operations
    if (isSaving) {
      return;
    }

    isSaving = true;
    try {
      // Get fresh state when the timeout fires
      const store = getStore();
      await store.saveDatabase();
    } catch (err) {
      console.error("Auto-save failed:", err);
    } finally {
      isSaving = false;
    }
  }, AUTO_SAVE_DELAY);
}

// ============================================================================
// Store
// ============================================================================

export const useLocalDbStore = create<LocalDbStore>((set, get) => ({
  ...initialState,

  // ==========================================================================
  // Database Operations
  // ==========================================================================

  openDatabase: async () => {
    // This now triggers file check flow - encrypted files need password dialog
    set({ loading: true, error: null });

    try {
      const checkResult = await pickAndCheckDatabaseFile();

      if (checkResult.isEncrypted) {
        // Store pending check and wait for password from UI
        set({
          pendingFileCheck: checkResult,
          loading: false,
        });
        // UI will show password dialog and call openEncryptedDatabase
        return;
      }

      // Not encrypted - open directly
      const result = await openDatabaseWithPassword(checkResult);
      const classes = getAllClasses(result.db);

      set({
        db: result.db,
        fileHandle: result.handle as FileSystemFileHandleExt | null,
        fileName: result.fileName,
        isConnected: true,
        isDirty: false,
        lastSaved: new Date().toISOString(),
        isEncrypted: false,
        password: null,
        pendingFileCheck: null,
        classes,
        students: [],
        selectedClassId: null,
        sheetNumberMap: new Map(),
        loading: false,
      });

      // Auto-select first class if exists
      if (classes.length > 0) {
        get().selectClass(classes[0].id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to open database";
      set({ error: message, loading: false, pendingFileCheck: null });
    }
  },

  createDatabase: async () => {
    set({ loading: true, error: null });

    try {
      const db = await createNewDatabase();
      const result = await createNewDatabaseFile(db);

      set({
        db: result.db,
        fileHandle: result.handle as FileSystemFileHandleExt | null,
        fileName: result.fileName,
        isConnected: true,
        isDirty: false,
        lastSaved: new Date().toISOString(),
        isEncrypted: false,
        password: null,
        pendingFileCheck: null,
        classes: [],
        students: [],
        selectedClassId: null,
        sheetNumberMap: new Map(),
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create database";
      set({ error: message, loading: false });
    }
  },

  saveDatabase: async () => {
    const { db, fileHandle, isDirty, password } = get();

    if (!db) {
      throw new Error("No database to save");
    }

    // Only save if dirty (unless forcing save for new file)
    if (!isDirty && fileHandle) {
      return;
    }

    try {
      // Pass password for encrypted databases
      const newHandle = await saveDatabaseFile(db, fileHandle, password);

      set({
        fileHandle: newHandle as FileSystemFileHandleExt | null,
        fileName: newHandle?.name || get().fileName,
        isDirty: false,
        lastSaved: new Date().toISOString(),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save database";
      set({ error: message });
      throw err;
    }
  },

  closeDatabase: () => {
    const { db } = get();

    if (db) {
      db.close();
    }

    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }

    set(initialState);
  },

  // ==========================================================================
  // Encryption Operations
  // ==========================================================================

  checkDatabaseFile: async () => {
    set({ loading: true, error: null });

    try {
      const checkResult = await pickAndCheckDatabaseFile();
      set({
        pendingFileCheck: checkResult,
        loading: false,
      });
      return checkResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to check database file";
      set({ error: message, loading: false, pendingFileCheck: null });
      return null;
    }
  },

  openEncryptedDatabase: async (password: string) => {
    const { pendingFileCheck } = get();

    if (!pendingFileCheck) {
      set({ error: "No pending file to open" });
      return;
    }

    set({ loading: true, error: null });

    try {
      const result = await openDatabaseWithPassword(pendingFileCheck, password);
      const classes = getAllClasses(result.db);

      set({
        db: result.db,
        fileHandle: result.handle as FileSystemFileHandleExt | null,
        fileName: result.fileName,
        isConnected: true,
        isDirty: false,
        lastSaved: new Date().toISOString(),
        isEncrypted: true,
        password, // Store in memory for auto-save
        pendingFileCheck: null,
        failedPasswordAttempts: 0, // Reset on success
        classes,
        students: [],
        selectedClassId: null,
        sheetNumberMap: new Map(),
        loading: false,
      });

      // Auto-select first class if exists
      if (classes.length > 0) {
        get().selectClass(classes[0].id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to open encrypted database";
      set((state) => ({
        error: message,
        loading: false,
        failedPasswordAttempts: state.failedPasswordAttempts + 1,
      }));
      // Don't clear pendingFileCheck - allow retry with different password
    }
  },

  createEncryptedDatabase: async (password: string) => {
    set({ loading: true, error: null });

    try {
      const db = await createNewDatabase();
      const result = await createNewDatabaseFile(db, password);

      set({
        db: result.db,
        fileHandle: result.handle as FileSystemFileHandleExt | null,
        fileName: result.fileName,
        isConnected: true,
        isDirty: false,
        lastSaved: new Date().toISOString(),
        isEncrypted: true,
        password, // Store in memory for auto-save
        pendingFileCheck: null,
        classes: [],
        students: [],
        selectedClassId: null,
        sheetNumberMap: new Map(),
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create encrypted database";
      set({ error: message, loading: false });
    }
  },

  enableEncryption: async (password: string) => {
    const { db, fileHandle } = get();

    if (!db) {
      throw new Error("No database to encrypt");
    }

    set({ loading: true, error: null });

    try {
      // Save the database with encryption
      const newHandle = await saveDatabaseFile(db, fileHandle, password);

      set({
        fileHandle: newHandle as FileSystemFileHandleExt | null,
        fileName: newHandle?.name || get().fileName,
        isEncrypted: true,
        password,
        isDirty: false,
        lastSaved: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to encrypt database";
      set({ error: message, loading: false });
      throw err;
    }
  },

  lockDatabase: () => {
    // Clear password from memory and close database for security
    const { db } = get();
    if (db) {
      db.close();
    }
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    set(initialState);
  },

  clearPendingFileCheck: () => {
    set({ pendingFileCheck: null, error: null });
  },

  clearPassword: () => {
    set({ password: null });
  },

  // ==========================================================================
  // Class Operations
  // ==========================================================================

  selectClass: (classId: string | null) => {
    const { db } = get();

    if (!db || !classId) {
      set({ selectedClassId: null, students: [], sheetNumberMap: new Map() });
      return;
    }

    const students = getStudentsByClass(db, classId);
    const sheetNumberMap = new Map<string, { name: string; id: string }>();

    for (const student of students) {
      sheetNumberMap.set(student.sheetNumber, {
        name: student.name,
        id: student.id,
      });
    }

    set({
      selectedClassId: classId,
      students,
      sheetNumberMap,
    });
  },

  addClass: (input: CreateClassInput) => {
    const { db } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    const newClass = createClass(db, input);
    const classes = getAllClasses(db);

    set({ classes });
    get().markDirty();

    return newClass;
  },

  editClass: (id: string, input: Partial<CreateClassInput>) => {
    const { db } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    updateClass(db, id, input);
    const classes = getAllClasses(db);

    set({ classes });
    get().markDirty();
  },

  removeClass: (id: string) => {
    const { db, selectedClassId } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    deleteClass(db, id);
    const classes = getAllClasses(db);

    set({ classes });

    // If we deleted the selected class, clear selection
    if (selectedClassId === id) {
      get().selectClass(classes.length > 0 ? classes[0].id : null);
    }

    get().markDirty();
  },

  // ==========================================================================
  // Student Operations
  // ==========================================================================

  addStudent: (classId: string, name: string, sheetNumber?: string) => {
    const { db, selectedClassId } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    const newStudent = createStudent(db, { classId, name, sheetNumber });

    // Refresh students if we're viewing this class
    if (selectedClassId === classId) {
      get().selectClass(classId);
    }

    get().markDirty();
    return newStudent;
  },

  editStudent: (id: string, input: UpdateStudentInput) => {
    const { db, selectedClassId, students } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    updateStudent(db, id, input);

    // Refresh students to get updated data
    const student = students.find((s) => s.id === id);
    if (student && selectedClassId === student.classId) {
      get().selectClass(selectedClassId);
    }

    get().markDirty();
  },

  removeStudent: (id: string) => {
    const { db, selectedClassId, students } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    const student = students.find((s) => s.id === id);
    deleteStudent(db, id);

    // Refresh students if we're viewing this class
    if (student && selectedClassId === student.classId) {
      get().selectClass(selectedClassId);
    }

    get().markDirty();
  },

  importStudents: (classId: string, studentsData: Array<{ name: string; sheetNumber?: string }>) => {
    const { db, selectedClassId } = get();

    if (!db) {
      throw new Error("No database connected");
    }

    const newStudents = bulkCreateStudents(db, classId, studentsData);

    // Refresh students if we're viewing this class
    if (selectedClassId === classId) {
      get().selectClass(classId);
    }

    get().markDirty();
    return newStudents;
  },

  // ==========================================================================
  // Lookup Helpers
  // ==========================================================================

  getStudentBySheetNumber: (sheetNumber: string) => {
    const { sheetNumberMap } = get();
    return sheetNumberMap.get(sheetNumber) || null;
  },

  refreshSheetNumberMap: () => {
    const { selectedClassId } = get();
    if (selectedClassId) {
      get().selectClass(selectedClassId);
    }
  },

  // ==========================================================================
  // State Management
  // ==========================================================================

  markDirty: () => {
    set({ isDirty: true });

    // Schedule auto-save if File System Access API is supported
    if (isFileSystemAccessSupported() && get().fileHandle) {
      scheduleSave(() => get());
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

// ============================================================================
// Selectors
// ============================================================================

/**
 * Get database state for UI display
 */
export function useDatabaseState(): DatabaseState {
  const { isConnected, fileName, lastSaved, classes, students, isEncrypted } = useLocalDbStore();

  return {
    isConnected,
    fileName,
    classCount: classes.length,
    studentCount: students.length,
    lastSaved,
    isEncrypted,
  };
}

/**
 * Check if browser supports persistent file handles
 */
export function useSupportsFileSystemAccess(): boolean {
  return isFileSystemAccessSupported();
}
